import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  Badge,
  FlexCenter,
  FlexColumn,
  TableCell,
  TableRow,
  TextBody,
  TextOverflowTooltip,
  Tooltip,
} from "@opensea/ui-kit"
import { Info, WarningFilled } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { QUOTE_RECEIPT_TABLE_COLUMN_CLASSNAMES } from "../QuoteReceipt/constants"
import { CollectionSweepQuoteReceiptFragment } from "./CollectionSweepQuoteReceipt.fragment"
import {
  CollectionSweepQuoteReceiptRange,
  getPriceRangeFromQuote,
} from "./CollectionSweepQuoteReceiptRange"

type Props = {
  maxAmountOfItems: number
  quote: FragmentOf<typeof CollectionSweepQuoteReceiptFragment>
}

const MAX_PRICE_RANGE_SPREAD = 20

function getPriceRangeSpread(min: number, max: number) {
  if (min === max || max === 0) {
    return 0
  }
  return ((max - min) / max) * 100
}

export const CollectionSweepQuoteReceiptTotal = ({
  maxAmountOfItems,
  quote: quoteKey,
}: Props) => {
  const quote = readFragment(CollectionSweepQuoteReceiptFragment, quoteKey)
  const t = useTranslations("wallet.CollectionSweepFlow")

  const priceRange = getPriceRangeFromQuote(
    quote,
    maxAmountOfItems,
    item => item.pricePerItem.token.unit,
  )

  const priceRangeSpread = getPriceRangeSpread(priceRange.min, priceRange.max)
  const warnWideQuoteSpread = priceRangeSpread > MAX_PRICE_RANGE_SPREAD

  return (
    <TableRow>
      <TableCell className={QUOTE_RECEIPT_TABLE_COLUMN_CLASSNAMES.item}>
        <FlexCenter className="gap-1">
          <TextOverflowTooltip>
            <TextBody>{t("total")}</TextBody>
          </TextOverflowTooltip>
          <Tooltip
            align="start"
            content={
              <TextBody className="text-secondary" size="sm">
                <FlexColumn className="max-w-[260px] gap-2">
                  <TextBody size="sm">{t("tooltipUpperPriceRange1")}</TextBody>
                  {warnWideQuoteSpread ? (
                    <TextBody className="font-bold" size="sm">
                      {t("wideQuoteSpreadWarning")}
                    </TextBody>
                  ) : (
                    <TextBody className="font-bold" size="sm">
                      {t("tooltipUpperPriceRange2")}
                    </TextBody>
                  )}

                  <CollectionSweepQuoteReceiptRange
                    inTooltip={true}
                    maxAmountOfItems={maxAmountOfItems}
                    quote={quote}
                  />
                </FlexColumn>
              </TextBody>
            }
          >
            {warnWideQuoteSpread ? (
              <Badge icon={WarningFilled} variant="warning">
                {t("wideQuoteSpread", {
                  percent: priceRangeSpread.toPrecision(4),
                })}
              </Badge>
            ) : (
              <Info className="size-5" />
            )}
          </Tooltip>
        </FlexCenter>
      </TableCell>
      <TableCell className={QUOTE_RECEIPT_TABLE_COLUMN_CLASSNAMES.price}>
        <CollectionSweepQuoteReceiptRange
          maxAmountOfItems={maxAmountOfItems}
          quote={quote}
        />
      </TableCell>
    </TableRow>
  )
}
